import { useMemo } from "react";
import calcPaymentValue, {
  calculateCouponDiscountValue,
  couponDiscount,
  installmentInterestValue,
} from "../utils/calcPaymentValue";

const PIX_METHOD = "PIX";

export default function useCheckoutPricing({
  offer,
  wasPreviousStudent,
  discountConfirmed,
  coupon,
  installmentCount,
  billingType,
}) {
  return useMemo(() => {
    const paymentValue = calcPaymentValue(
      discountConfirmed ? wasPreviousStudent : null,
      offer,
      coupon,
      installmentCount,
      billingType
    );

    const paymentValueWithoutPixDiscount = calcPaymentValue(
      discountConfirmed ? wasPreviousStudent : null,
      offer,
      coupon,
      installmentCount,
      null
    );

    const paymentValueWithoutCouponDiscount = discountConfirmed
      ? calcPaymentValue(wasPreviousStudent, offer, null, null, billingType)
      : offer?.price;

    const interestValue = installmentInterestValue(
      discountConfirmed ? wasPreviousStudent : null,
      offer,
      coupon,
      installmentCount,
      billingType
    );

    const hasBonusCourses = !!offer?.meta?.bonusCourses?.length;
    const hasStudentDiscount =
      !!discountConfirmed && offer?.hasStudentDiscount && !!wasPreviousStudent;
    const hasInterest = interestValue > 0;
    const isPixDiscountApplicable =
      billingType === PIX_METHOD && offer?.pixDiscount > 0;

    const studentDiscountValue = hasStudentDiscount
      ? offer?.price - offer?.resellPrice
      : 0;

    const priceAfterStudentDiscount =
      offer?.price - (hasStudentDiscount ? studentDiscountValue : 0);
    const baseCouponDiscount = calculateCouponDiscountValue(
      coupon,
      offer?.price
    );

    // What the student discount would be worth, regardless of the customer
    // having confirmed it yet. `studentDiscountValue` is zeroed out until
    // `discountConfirmed`, so it cannot answer whether the discount is still
    // worth offering.
    const potentialStudentDiscountValue =
      offer?.hasStudentDiscount && wasPreviousStudent
        ? offer?.price - offer?.resellPrice
        : 0;

    // A non-cumulative coupon worth at least as much as the student discount
    // replaces it entirely, so offering the student discount is pointless.
    const isStudentDiscountSupersededByCoupon =
      !!offer?.couponEnabled &&
      !!coupon?.nonCumulative &&
      potentialStudentDiscountValue > 0 &&
      baseCouponDiscount >= potentialStudentDiscountValue;

    const couponDiscountValue = couponDiscount(
      coupon,
      offer,
      priceAfterStudentDiscount,
      studentDiscountValue
    );

    const isStudentDiscountActive =
      studentDiscountValue > 0 &&
      (!coupon ||
        !coupon.nonCumulative ||
        (coupon.nonCumulative && studentDiscountValue > baseCouponDiscount));

    const isNonCumulativeCouponRejected =
      coupon?.nonCumulative && baseCouponDiscount < studentDiscountValue;

    const isNonCumulativeCouponAccepted =
      coupon?.nonCumulative && baseCouponDiscount >= studentDiscountValue;

    const isCouponApplicable =
      offer?.couponEnabled &&
      coupon &&
      (!coupon.nonCumulative || isNonCumulativeCouponAccepted);

    const pixDiscountValue = isPixDiscountApplicable
      ? paymentValueWithoutPixDiscount * (offer.pixDiscount / 100)
      : 0;

    return {
      paymentValue,
      paymentValueWithoutPixDiscount,
      paymentValueWithoutCouponDiscount,
      interestValue,
      pixDiscountValue,
      studentDiscountValue,
      potentialStudentDiscountValue,
      couponDiscountValue,
      hasBonusCourses,
      hasStudentDiscount,
      hasInterest,
      isPixDiscountApplicable,
      isStudentDiscountActive,
      isStudentDiscountSupersededByCoupon,
      isCouponApplicable,
      isNonCumulativeCouponRejected,
      isNonCumulativeCouponAccepted,
    };
  }, [
    offer,
    wasPreviousStudent,
    discountConfirmed,
    coupon,
    installmentCount,
    billingType,
  ]);
}
